import { push, set } from 'firebase/database'

import { useGameContext } from 'features/game/game-context'
import { getGameClock } from 'features/game/utils'

import { gamesRef } from './database'
import {
  DeployMetadata,
  DrawMetadata,
  GameEvent,
  MoveMetadata,
  PossibleMove,
  SwitchMetadata,
  WithdrawMetadata,
} from './types'

export const useGameEvents = () => {
  const {
    game: { gameId, events, timeToPlay },
    turnDuration,
    turn,
    resetTurn,
  } = useGameContext()

  const eventsRef = gamesRef(`all/${gameId}/events`)

  const dispatchEvent = async (event: Omit<GameEvent, 'clock'>) => {
    const clock = getGameClock(events, timeToPlay)
    const newClock = {
      ...clock,
      [turn]: clock[turn] - turnDuration,
    }

    resetTurn()
    await push(eventsRef, { ...event, clock: newClock })
  }

  const dispatchMoveEvent = (metadata: MoveMetadata) => dispatchEvent({ type: 'move', metadata })
  const dispatchSwitchEvent = (metadata: SwitchMetadata) => dispatchEvent({ type: 'switch', metadata })
  const dispatchWithdrawEvent = (metadata: WithdrawMetadata) => dispatchEvent({ type: 'withdraw', metadata })
  const dispatchDeployEvent = (metadata: DeployMetadata) => dispatchEvent({ type: 'deploy', metadata })
  const dispatchDistributeEvent = (metadata: DeployMetadata) => dispatchEvent({ type: 'distribute', metadata })
  const dispatchDrawEvent = (metadata: DrawMetadata) => dispatchEvent({ type: 'draw', metadata })
  const dispatchRawEvent = (event: PossibleMove) => dispatchEvent({ type: event.type, metadata: event.metadata })
  const dispatchFightEvent = () => dispatchEvent({ type: 'fight', metadata: null })

  const setRawEvents = async (events: Object) => await set(eventsRef, events)

  return {
    dispatchRawEvent,
    setRawEvents,
    dispatchDistributeEvent,
    dispatchDeployEvent,
    dispatchMoveEvent,
    dispatchWithdrawEvent,
    dispatchSwitchEvent,
    dispatchFightEvent,
    dispatchDrawEvent,
  }
}
